import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Check, Car, Calendar, Clock, CreditCard, QrCode, Download, ChevronRight, Loader2, Smartphone, Phone, FileText } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { reservationsApi, paymentsApi, invoicesApi } from '../lib/api';

const CheckoutSuccessPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [qrType, setQrType] = useState('CHECKIN');
  const qrRef = useRef(null);

  const sessionId = searchParams.get('session_id');
  const reservationId = searchParams.get('reservation_id');
  const method = searchParams.get('method');
  const locale = i18n.language === 'es' ? es : enUS;

  // Auto-show QR on page load
  useEffect(() => {
    if (reservation?.qr_checkin_token) {
      setShowQR(true);
    }
  }, [reservation]);

  // Function to download QR as PNG
  const downloadQR = () => {
    if (!qrRef.current || !reservation) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Add padding and branding
    const padding = 40;
    const textHeight = 80;
    canvas.width = 200 + padding * 2;
    canvas.height = 200 + padding * 2 + textHeight;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR
      ctx.drawImage(img, padding, padding, 200, 200);
      
      // Add text
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('PLEYADES PARKING', canvas.width / 2, 200 + padding + 30);
      ctx.font = '14px Arial';
      ctx.fillText(`${qrType === 'CHECKIN' ? 'CHECK-IN' : 'CHECK-OUT'} - ${reservation.reservation_code}`, canvas.width / 2, 200 + padding + 55);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(reservation.vehicle_plate, canvas.width / 2, 200 + padding + 75);
      
      // Download
      const link = document.createElement('a');
      link.download = `pleyades-${qrType.toLowerCase()}-${reservation.reservation_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(i18n.language === 'es' ? 'QR guardado en tu dispositivo' : 'QR saved to your device');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        // If Stripe payment, poll status first
        if (sessionId) {
          let attempts = 0;
          while (attempts < 5) {
            const statusResponse = await paymentsApi.getCheckoutStatus(sessionId);
            if (statusResponse.data.payment_status === 'paid') {
              break;
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempts++;
          }
        }

        // Get reservation details
        if (reservationId) {
          const response = await reservationsApi.getById(reservationId);
          setReservation(response.data);
        }
      } catch (error) {
        console.error('Error fetching reservation:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [sessionId, reservationId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-white">{t('checkout.processing')}</p>
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Reserva no encontrada</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "dd MMM yyyy 'a las' HH:mm", { locale });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-heavy">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">PLEYADES</span>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              {t('checkout.success')}
            </h1>
            <p className="text-zinc-400">
              {method === 'cash' 
                ? (i18n.language === 'es' ? 'Recuerda pagar en efectivo en el parking' : 'Remember to pay cash at the parking')
                : method === 'tpv'
                ? (i18n.language === 'es' ? 'Recuerda pagar con tarjeta o Bizum en el parking' : 'Remember to pay by card or Bizum at the parking')
                : (i18n.language === 'es' ? 'Tu pago ha sido procesado correctamente' : 'Your payment has been processed successfully')}
            </p>
          </motion.div>

          {/* Reservation Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-dark p-6 mb-6"
          >
            {/* Reservation Code */}
            <div className="text-center pb-6 border-b border-white/10">
              <p className="text-zinc-400 text-sm mb-2">{t('checkout.code')}</p>
              <p className="font-mono text-3xl font-bold text-primary tracking-wider" data-testid="reservation-code">
                {reservation.reservation_code}
              </p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-zinc-400 text-sm">{t('checkout.arrival')}</p>
                  <p className="text-white">{formatDate(reservation.start_dt)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-zinc-400 text-sm">{t('checkout.departure')}</p>
                  <p className="text-white">{formatDate(reservation.end_dt)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Car className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-zinc-400 text-sm">{t('checkout.vehicle')}</p>
                  <p className="text-white font-mono">{reservation.vehicle_plate}</p>
                  {reservation.vehicle_brand && (
                    <p className="text-zinc-500 text-sm">
                      {reservation.vehicle_brand} {reservation.vehicle_model}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-zinc-400 text-sm">{t('booking.total')}</p>
                  <p className="text-white font-bold text-xl">
                    {(reservation.total_cents / 100).toFixed(2)}€
                  </p>
                  <p className={`text-sm ${reservation.payment_status === 'PAID' ? 'text-success' : 'text-warning'}`}>
                    {t(`status.${reservation.payment_status}`)}
                  </p>
                </div>
              </div>
            </div>

            {/* QR Button */}
            <div className="pt-6 border-t border-white/10">
              <Button
                onClick={() => setShowQR(!showQR)}
                className="w-full btn-primary"
                data-testid="show-qr-btn"
              >
                <QrCode className="w-5 h-5 mr-2" />
                {t('checkout.showQR')}
              </Button>
            </div>
          </motion.div>

          {/* QR Codes Section */}
          {showQR && reservation.qr_checkin_token && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-dark p-6 mb-6"
            >
              <div className="flex gap-4 mb-6 justify-center">
                <Button
                  onClick={() => setQrType('CHECKIN')}
                  className={qrType === 'CHECKIN' ? 'btn-primary' : 'btn-secondary'}
                  data-testid="qr-checkin-tab"
                >
                  Check-in
                </Button>
                <Button
                  onClick={() => setQrType('CHECKOUT')}
                  className={qrType === 'CHECKOUT' ? 'btn-primary' : 'btn-secondary'}
                  data-testid="qr-checkout-tab"
                >
                  Check-out
                </Button>
              </div>

              <div className="flex justify-center" ref={qrRef}>
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeSVG
                    value={qrType === 'CHECKIN' ? reservation.qr_checkin_token : reservation.qr_checkout_token}
                    size={200}
                    level="H"
                    data-testid="qr-code"
                  />
                </div>
              </div>

              <p className="text-center text-zinc-400 text-sm mt-4">
                {qrType === 'CHECKIN' 
                  ? (i18n.language === 'es' ? 'Muestra este QR a tu conductor' : 'Show this QR to your driver')
                  : (i18n.language === 'es' ? 'Muestra este QR a tu conductor' : 'Show this QR to your driver')}
              </p>

              {/* Reminder & Call Button */}
              <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-center text-primary text-sm font-medium mb-3">
                  {i18n.language === 'es' 
                    ? 'Recuerda llamar 30 minutos antes de llegar al aeropuerto' 
                    : 'Remember to call 30 minutes before arriving at the airport'}
                </p>
                <a
                  href="tel:641243287"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-success hover:bg-success/90 text-white font-bold rounded-lg transition-colors"
                  data-testid="call-button"
                >
                  <Phone className="w-5 h-5" />
                  {i18n.language === 'es' ? 'Llamar ahora: 641 243 287' : 'Call now: 641 243 287'}
                </a>
              </div>

              {/* Download Button */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={downloadQR}
                  className="btn-secondary"
                  data-testid="download-qr-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  {i18n.language === 'es' ? 'Guardar QR' : 'Save QR'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Invoice Download Section - shown prominently if user requested invoice */}
          {reservation.wants_invoice && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-dark p-6 mb-6 border-2 border-primary/30"
            >
              <div className="text-center">
                <FileText className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">
                  {i18n.language === 'es' ? 'Tu Factura está Lista' : 'Your Invoice is Ready'}
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  {i18n.language === 'es' 
                    ? `Datos de facturación: ${reservation.billing_company_name} (${reservation.billing_cif})`
                    : `Billing details: ${reservation.billing_company_name} (${reservation.billing_cif})`}
                </p>
                <Button
                  onClick={async () => {
                    try {
                      await invoicesApi.generate(reservation.reservation_code);
                      window.open(invoicesApi.downloadPdf(reservation.reservation_code), '_blank');
                      toast.success(i18n.language === 'es' ? 'Factura descargada' : 'Invoice downloaded');
                    } catch (error) {
                      toast.error(i18n.language === 'es' ? 'Error al generar factura' : 'Error generating invoice');
                    }
                  }}
                  className="btn-primary"
                  data-testid="download-invoice-btn-main"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {i18n.language === 'es' ? 'Descargar Factura PDF' : 'Download Invoice PDF'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/account')}
              className="btn-secondary"
              data-testid="go-to-account-btn"
            >
              {t('nav.account')}
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="btn-ghost"
              data-testid="go-home-btn"
            >
              {t('nav.home')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
